import React, { useEffect, useState } from 'react';
import { Save, Send, Check, AlertCircle, Mail } from 'lucide-react';
import { api } from '../../lib/api';

export default function SmtpSettings() {
  const [cfg, setCfg] = useState({
    host: '', port: 587, username: '', password: '',
    from_email: '', from_name: '', use_tls: true, enabled: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [test, setTest] = useState({ to: '', running: false, ok: null, msg: '' });

  useEffect(() => {
    api.get('/admin/settings/smtp')
      .then(({ data }) => setCfg({ ...cfg, ...(data?.value || {}) }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings/smtp', { value: cfg });
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    if (!test.to) return;
    setTest((s) => ({ ...s, running: true, ok: null, msg: '' }));
    try {
      // Save first to ensure latest values are used
      await api.put('/admin/settings/smtp', { value: cfg });
      const { data } = await api.post('/admin/settings/smtp/test', { to: test.to });
      setTest((s) => ({ ...s, running: false, ok: !!data.ok, msg: data.info || '' }));
    } catch (e) {
      setTest((s) => ({ ...s, running: false, ok: false, msg: e.response?.data?.detail || 'Error' }));
    }
  };

  if (loading) return <div className="text-ink-muted py-20 text-center">Cargando…</div>;

  return (
    <div data-testid="smtp-settings">
      <div className="flex items-center justify-between mb-8 pb-6 border-b border-line">
        <div>
          <p className="text-eyebrow text-ink-muted mb-2">Configuración</p>
          <h1 className="font-display text-4xl md:text-5xl text-ink">SMTP</h1>
          <p className="text-ink-muted mt-2 max-w-2xl">Configura tu servidor SMTP para enviar emails desde el formulario de contacto. Los datos se guardan cifrados en la base de datos, no en el código.</p>
        </div>
        <button onClick={save} disabled={saving} className="btn-acid !px-6 !py-2.5 !text-[11px]" data-testid="smtp-save">
          <Save size={13} /> {saving ? 'Guardando…' : 'Guardar'}
        </button>
      </div>

      {savedAt && (
        <div className="mb-6 px-4 py-3 bg-acid/10 border border-acid/40 text-acid rounded-md text-sm font-mono flex items-center gap-2">
          <Check size={14} /> Configuración guardada.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 bg-bg-secondary border border-line rounded-md p-6 md:p-8">
        <div>
          <label className="text-eyebrow mb-1 block">Estado</label>
          <label className="flex items-center justify-between bg-bg border border-line rounded-md px-4 py-3 cursor-pointer">
            <span className="text-sm text-ink font-mono">SMTP activo</span>
            <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} className="w-5 h-5 accent-acid" data-testid="smtp-enabled" />
          </label>
        </div>
        <div>
          <label className="text-eyebrow mb-1 block">Use TLS / STARTTLS</label>
          <label className="flex items-center justify-between bg-bg border border-line rounded-md px-4 py-3 cursor-pointer">
            <span className="text-sm text-ink font-mono">Cifrado activo</span>
            <input type="checkbox" checked={cfg.use_tls} onChange={(e) => setCfg({ ...cfg, use_tls: e.target.checked })} className="w-5 h-5 accent-acid" data-testid="smtp-use-tls" />
          </label>
        </div>
        <div>
          <label className="text-eyebrow mb-1 block">Host</label>
          <input type="text" value={cfg.host || ''} onChange={(e) => setCfg({ ...cfg, host: e.target.value })} className="input-bare" placeholder="smtp.gmail.com" data-testid="smtp-host" />
        </div>
        <div>
          <label className="text-eyebrow mb-1 block">Puerto</label>
          <input type="number" value={cfg.port || 587} onChange={(e) => setCfg({ ...cfg, port: Number(e.target.value) })} className="input-bare" placeholder="587 / 465" data-testid="smtp-port" />
        </div>
        <div>
          <label className="text-eyebrow mb-1 block">Usuario</label>
          <input type="text" value={cfg.username || ''} onChange={(e) => setCfg({ ...cfg, username: e.target.value })} className="input-bare" autoComplete="off" data-testid="smtp-username" />
        </div>
        <div>
          <label className="text-eyebrow mb-1 block">Contraseña</label>
          <input type="password" value={cfg.password || ''} onChange={(e) => setCfg({ ...cfg, password: e.target.value })} className="input-bare" autoComplete="new-password" data-testid="smtp-password" />
        </div>
        <div>
          <label className="text-eyebrow mb-1 block">Email remitente</label>
          <input type="email" value={cfg.from_email || ''} onChange={(e) => setCfg({ ...cfg, from_email: e.target.value })} className="input-bare" placeholder="no-reply@modaria.com" data-testid="smtp-from-email" />
        </div>
        <div>
          <label className="text-eyebrow mb-1 block">Nombre remitente</label>
          <input type="text" value={cfg.from_name || ''} onChange={(e) => setCfg({ ...cfg, from_name: e.target.value })} className="input-bare" placeholder="MODARIA" data-testid="smtp-from-name" />
        </div>
      </div>

      <div className="mt-10 bg-bg-secondary border border-line rounded-md p-6 md:p-8" data-testid="smtp-test">
        <div className="flex items-center gap-2 mb-2">
          <Mail size={16} className="text-acid" />
          <h2 className="font-display text-2xl text-ink">Probar configuración</h2>
        </div>
        <p className="text-ink-muted text-sm mb-5 max-w-xl">Envía un email de prueba al destinatario que indiques. Guarda primero los cambios.</p>
        <div className="flex flex-col md:flex-row gap-3">
          <input type="email" placeholder="tu@email.com" value={test.to} onChange={(e) => setTest({ ...test, to: e.target.value })} className="input-bare flex-1" data-testid="smtp-test-to" />
          <button onClick={runTest} disabled={test.running || !test.to} className="btn-acid !px-6 !py-2.5 !text-[11px] disabled:opacity-50" data-testid="smtp-test-send">
            <Send size={13} /> {test.running ? 'Enviando…' : 'Enviar test'}
          </button>
        </div>
        {test.ok === true && <div className="mt-4 text-acid text-sm font-mono flex items-center gap-2"><Check size={14} /> Email enviado correctamente.</div>}
        {test.ok === false && <div className="mt-4 text-danger text-sm font-mono flex items-center gap-2"><AlertCircle size={14} /> {test.msg}</div>}
      </div>
    </div>
  );
}
